/**
 * Generic vertical scroll-snap card feed: one card per item, each ≈ viewport
 * height. The card snapped to centre becomes the focus and its real scene is
 * loaded into the running engine in-place (no page reload) so it plays live
 * behind a transparent window punched through the card; every other card shows
 * its dynamically-captured still and covers the canvas. Camera drag is
 * suspended while the feed is open (consumer.onEnter) and the visitor's edited
 * scene is stashed, then restored in-place on close (consumer.onExit). Focus
 * switches the instant the most-visible card changes (even mid-scroll) and
 * neighbour scenes are prefetched so scrolling does not stall on the network.
 *
 * The feed is thumbnail-agnostic: exactly one card renders live at a time and a
 * still is captured dynamically as it scrolls out, so a data source with no
 * stored thumbnails works unchanged. A consumer supplies the item source, the
 * card-info / index-row DOM, scene fetch, the commit action, and chrome flags;
 * the core owns the layout, focus, scroll physics, preview cache and lifecycle.
 */

const FEED_DWELL_MS = 5000;
const FEED_PREFETCH_RADIUS = 3;
const FEED_CACHE_MAX = 12;
const SNAP_COMMIT_FRACTION = 0.1;

export type FeedCoreItem = {
  id: string;
  title: string;
  owner: string;
  ownerName: string | null;
  mine: boolean;
  status: string;
};

export type FeedLoadResult<T> = { items: T[] } | { error: string };
export type FeedSceneResult = { scene: unknown } | { error: string };

/**
 * Loading spinner control handed to onSettle. show puts the spinner over the
 * card's stage; hide(id) only clears it when id is still the settled card, so a
 * settle whose async work resolves after the user scrolled on cannot wipe a
 * newer card's spinner.
 */
export interface FeedSpinner {
  show: (id: string) => void;
  hide: (id: string) => void;
}

/**
 * Services the core exposes to its consumer. The consumer is built via a
 * factory that receives this so its search controls / index rows / actions can
 * drive the core (reload, navigate, set status) without the core knowing the
 * consumer's concrete shape.
 */
export interface FeedCoreApi<T extends FeedCoreItem> {
  reload: () => Promise<void>;
  goToCard: (id: string) => void;
  close: () => void;
  setStatus: (msg: string) => void;
  setToast: (msg: string) => void;
  getPreview: (id: string) => string | undefined;
  getItem: (id: string) => T | undefined;
  prefetchScene: (id: string) => Promise<unknown | null>;
  neighbourIds: (id: string) => string[];
  closeCardMenus: () => void;
  closeIndexDrawer: () => void;
  isClosed: () => boolean;
}

/**
 * Per-consumer configuration. The generic part of an item (id/title/owner/
 * ownerName/mine/status) is the only thing the core reads; everything specific
 * (counters, tags, mic, search query) lives in the consumer's builders/actions.
 */
export interface FeedConsumer<T extends FeedCoreItem> {
  title: string;
  chrome: { search: boolean; index: boolean; count: boolean };
  searchControls: HTMLElement | null;
  strings: {
    loading: string;
    empty: string;
    loadFailed: string;
    indexToggleAria: string;
    indexCount: (n: number) => string;
  };
  onEnter: () => void;
  onExit: () => void;
  onClose: () => void;
  loadItems: () => Promise<FeedLoadResult<T>>;
  fetchScene: (id: string) => Promise<FeedSceneResult>;
  applySceneInPlace: (scene: unknown) => string | null;
  setViewport: (stage: HTMLElement | null) => void;
  capturePreview: (id: string) => Promise<string | null>;
  buildCardInfo: (item: T) => HTMLElement;
  buildIndexRow: (item: T) => HTMLButtonElement;
  buildCardStageOverlay?: (item: T) => HTMLElement | null;
  cardClassName?: (item: T) => string | null;
  buildCardMenu?: (item: T) => HTMLElement | null;
  onPreviewCaptured?: (id: string, dataUrl: string) => void;
  onDwell?: (id: string) => void;
  onSettle?: (item: T, scene: unknown, spinner: FeedSpinner) => void;
  guardCommit?: (item: T) => string | null;
  onCommit: (item: T, scene: unknown) => void;
}

export function openFeed<T extends FeedCoreItem>(makeConsumer: (api: FeedCoreApi<T>) => FeedConsumer<T>): void {
  const sceneCache = new Map<string, unknown>();
  const itemsById = new Map<string, T>();
  const cardEls = new Map<string, HTMLDivElement>();
  const stageEls = new Map<string, HTMLDivElement>();
  const previewEls = new Map<string, HTMLImageElement>();
  const indexRows = new Map<string, HTMLButtonElement>();
  const previewCache = new Map<string, string>();
  let orderedIds: string[] = [];
  let currentFocusId: string | null = null;
  let settledId: string | null = null;
  let closed = false;

  let spinnerTimer: number | undefined;
  let dwellTimer: number | undefined;
  let wheelTimer: number | undefined;
  let snapping = false;
  let dragging = false;
  let dragMoved = false;
  let dragStartY = 0;
  let dragStartTop = 0;
  let dragPointerId = -1;
  let dragVel = 0;
  let dragLastY = 0;
  let dragLastT = 0;
  let snapRaf = 0;
  let snapVelocity = 0;
  let toastTimer: number | undefined;

  const overlay = document.createElement("div");
  overlay.className = "feed-overlay";
  const scroll = document.createElement("div");
  scroll.className = "feed-scroll";

  const header = document.createElement("div");
  header.className = "feed-header";
  const titleEl = document.createElement("div");
  titleEl.className = "modal-title";

  const body = document.createElement("div");
  body.className = "feed-body";
  const index = document.createElement("aside");
  index.className = "feed-index";
  const indexCount = document.createElement("div");
  indexCount.className = "feed-index-count";
  const indexList = document.createElement("div");
  indexList.className = "feed-index-list";

  const status = document.createElement("div");
  status.className = "row-value scene-status feed-status";

  const toast = document.createElement("div");
  toast.className = "feed-toast";

  const cardSpinner = document.createElement("div");
  cardSpinner.className = "feed-card-spinner";
  cardSpinner.style.display = "none";
  cardSpinner.innerHTML =
    '<svg viewBox="0 0 50 50"><circle class="spinner-outline" cx="25" cy="25" r="20" /><circle class="spinner-arc" cx="25" cy="25" r="20" /></svg>';
  const SPINNER_DELAY_MS = 200;

  function setToastMessage(msg: string): void {
    if (toastTimer !== undefined) {
      window.clearTimeout(toastTimer);
      toastTimer = undefined;
    }
    toast.textContent = msg;
    if (msg === "") {
      return;
    }
    toastTimer = window.setTimeout((): void => {
      toast.textContent = "";
      toastTimer = undefined;
    }, 5000);
  }

  const api: FeedCoreApi<T> = {
    reload: (): Promise<void> => load(),
    goToCard: (id: string): void => goToCard(id),
    close: (): void => close(),
    setStatus: (msg: string): void => {
      status.textContent = msg;
    },
    setToast: (msg: string): void => {
      setToastMessage(msg);
    },
    getPreview: (id: string): string | undefined => previewCache.get(id),
    getItem: (id: string): T | undefined => itemsById.get(id),
    prefetchScene: (id: string): Promise<unknown | null> => prefetchScene(id),
    neighbourIds: (id: string): string[] => neighbourIds(id),
    closeCardMenus: (): void => closeCardMenus(),
    closeIndexDrawer: (): void => {
      overlay.classList.remove("feed-index-open");
    },
    isClosed: (): boolean => closed,
  };

  const consumer = makeConsumer(api);
  consumer.onEnter();

  titleEl.textContent = consumer.title;
  const indexToggle = document.createElement("button");
  indexToggle.className = "feed-index-toggle";
  indexToggle.textContent = "☰";
  indexToggle.setAttribute("aria-label", consumer.strings.indexToggleAria);
  indexToggle.addEventListener("click", (): void => {
    overlay.classList.toggle("feed-index-open");
  });
  if (consumer.chrome.index) {
    header.appendChild(indexToggle);
  }
  header.appendChild(titleEl);
  if (consumer.chrome.search && consumer.searchControls !== null) {
    header.appendChild(consumer.searchControls);
  }

  if (consumer.chrome.index) {
    index.append(indexCount, indexList);
    body.appendChild(index);
  }

  const spinner: FeedSpinner = {
    show: (id: string): void => showCardSpinner(id),
    hide: (id: string): void => {
      if (id === settledId) {
        hideCardSpinner();
      }
    },
  };

  /**
   * Centers the loading spinner over a card's stage while its settle work runs
   * (the sim is frozen meanwhile), but only after a short delay so a fast cache
   * hit does not flash the spinner. hideCardSpinner cancels a pending show, so a
   * load that finishes within the delay never shows it at all.
   */
  function showCardSpinner(id: string): void {
    window.clearTimeout(spinnerTimer);
    spinnerTimer = window.setTimeout((): void => {
      const stage = stageEls.get(id);
      if (stage === undefined) {
        return;
      }
      stage.appendChild(cardSpinner);
      cardSpinner.style.display = "flex";
    }, SPINNER_DELAY_MS);
  }

  function hideCardSpinner(): void {
    window.clearTimeout(spinnerTimer);
    spinnerTimer = undefined;
    cardSpinner.style.display = "none";
    cardSpinner.remove();
  }

  const FEED_BASE_GAP = 24;
  /**
   * Widens the inter-card gap so card-center to card-center spacing is at least
   * the viewport height. A narrow viewport caps the card width at 92vw, which
   * makes the card shorter than the view; without this, two or three short cards
   * share one screen and break the one-card-per-page snap pager.
   */
  function updateFeedGap(): void {
    const first = scroll.querySelector(".feed-card");
    if (!(first instanceof HTMLElement)) {
      return;
    }
    const cardH = first.getBoundingClientRect().height;
    const gap = Math.max(FEED_BASE_GAP, Math.round(scroll.clientHeight - cardH));
    scroll.style.setProperty("--feed-gap", `${gap}px`);
  }

  const feedHeightObserver = new ResizeObserver((): void => {
    scroll.style.setProperty("--feed-scroll-h", `${scroll.clientHeight}px`);
    updateFeedGap();
  });
  feedHeightObserver.observe(scroll);

  function close(): void {
    teardown(true);
  }

  /**
   * Single teardown for both the close (✕ / back) and commit (open the focused
   * scene) paths. restore re-applies the stashed visitor scene; the commit path
   * keeps the opened scene instead, so it tears down without restoring.
   */
  function teardown(restore: boolean): void {
    closed = true;
    consumer.onClose();
    feedHeightObserver.disconnect();
    cancelAnimationFrame(snapRaf);
    window.clearTimeout(dwellTimer);
    window.clearTimeout(wheelTimer);
    window.clearTimeout(spinnerTimer);
    if (toastTimer !== undefined) {
      window.clearTimeout(toastTimer);
      toastTimer = undefined;
    }
    overlay.remove();
    if (restore) {
      consumer.onExit();
    }
  }

  async function prefetchScene(id: string): Promise<unknown | null> {
    const cached = sceneCache.get(id);
    if (cached !== undefined) {
      return cached;
    }
    const res = await consumer.fetchScene(id);
    if ("error" in res) {
      return null;
    }
    sceneCache.set(id, res.scene);
    while (sceneCache.size > FEED_CACHE_MAX) {
      const oldest = sceneCache.keys().next().value;
      if (oldest === undefined || oldest === currentFocusId) {
        break;
      }
      sceneCache.delete(oldest);
    }
    return res.scene;
  }

  function prefetchAround(id: string): void {
    const idx = orderedIds.indexOf(id);
    if (idx < 0) {
      return;
    }
    for (let d = 1; d <= FEED_PREFETCH_RADIUS; d++) {
      const before = orderedIds[idx - d];
      const after = orderedIds[idx + d];
      if (before !== undefined) {
        void prefetchScene(before);
      }
      if (after !== undefined) {
        void prefetchScene(after);
      }
    }
  }

  function neighbourIds(id: string): string[] {
    const idx = orderedIds.indexOf(id);
    if (idx < 0) {
      return [];
    }
    const out: string[] = [];
    for (const d of [-1, 1]) {
      const nid = orderedIds[idx + d];
      if (nid !== undefined) {
        out.push(nid);
      }
    }
    return out;
  }

  function scheduleDwell(id: string): void {
    window.clearTimeout(dwellTimer);
    dwellTimer = window.setTimeout((): void => {
      if (closed || currentFocusId !== id) {
        return;
      }
      consumer.onDwell?.(id);
    }, FEED_DWELL_MS);
  }

  /**
   * Captures a still of the focused card once (via consumer.capturePreview) and
   * shows it on that card whenever it is later unfocused. Cards already holding a
   * still are skipped so a focus never re-shoots; the LRU keeps currentFocusId so
   * the live card's still is never evicted out from under it.
   */
  function requestPreview(id: string): void {
    if (previewCache.has(id)) {
      return;
    }
    void consumer.capturePreview(id).then((dataUrl: string | null): void => {
      if (closed || dataUrl === null || dataUrl === "") {
        return;
      }
      previewCache.set(id, dataUrl);
      while (previewCache.size > FEED_CACHE_MAX) {
        const oldest = previewCache.keys().next().value;
        if (oldest === undefined || oldest === currentFocusId) {
          break;
        }
        previewCache.delete(oldest);
      }
      consumer.onPreviewCaptured?.(id, dataUrl);
      const el = previewEls.get(id);
      if (el !== undefined) {
        el.src = dataUrl;
      }
    });
  }

  /**
   * Shoots the still only once the focused card has come to rest fully in view,
   * so the snapshot never bakes in the black of an off-screen edge (capturing
   * mid-scroll clipped the bottom of the card to black).
   */
  function maybeCapturePreview(): void {
    if (closed || currentFocusId === null) {
      return;
    }
    if (visibleFraction(currentFocusId) >= 0.98) {
      requestPreview(currentFocusId);
      fireSettle();
    }
  }

  /**
   * Notifies the consumer that a card has come fully to rest, gated on the same
   * full-rest threshold as the preview capture so a fast scroll (which never lets
   * a card come to rest) fires no settle — only the card the user lands on does.
   * Fires once per landed card; a skip when the scene is not yet cached is
   * recovered by the call in renderFocusedScene once it arrives.
   */
  function fireSettle(): void {
    if (closed || currentFocusId === null || settledId === currentFocusId || visibleFraction(currentFocusId) < 0.98) {
      return;
    }
    const item = itemsById.get(currentFocusId);
    const scene = sceneCache.get(currentFocusId);
    if (item === undefined || scene === undefined) {
      return;
    }
    settledId = currentFocusId;
    consumer.onSettle?.(item, scene, spinner);
  }

  function renderFocusedScene(id: string, scene: unknown): void {
    const err = consumer.applySceneInPlace(scene);
    if (err !== null) {
      status.textContent = err;
      return;
    }
    status.textContent = "";
    consumer.setViewport(stageEls.get(id) ?? null);
    fireSettle();
  }

  /**
   * Focus UI (focused class, neighbour prefetch, dwell timer) is committed
   * synchronously so scrolling never stalls on the network; the scene is drawn
   * immediately on a cache hit, otherwise the fetch resolves in the background
   * and the result is dropped unless this card is still the focus (stale guard
   * against a scene that arrives after the user scrolled past).
   */
  function commitFocus(id: string): void {
    if (id === currentFocusId) {
      return;
    }
    const prev = currentFocusId;
    currentFocusId = id;
    settledId = null;
    hideCardSpinner();
    if (prev !== null) {
      cardEls.get(prev)?.classList.remove("focused");
      indexRows.get(prev)?.classList.remove("active");
    }
    cardEls.get(id)?.classList.add("focused");
    const activeRow = indexRows.get(id);
    if (activeRow !== undefined) {
      activeRow.classList.add("active");
      activeRow.scrollIntoView({ block: "nearest" });
    }
    prefetchAround(id);
    scheduleDwell(id);
    const cached = sceneCache.get(id);
    if (cached !== undefined) {
      renderFocusedScene(id, cached);
      return;
    }
    void prefetchScene(id).then((scene: unknown | null): void => {
      if (closed || currentFocusId !== id) {
        return;
      }
      if (scene === null) {
        status.textContent = consumer.strings.loadFailed;
        return;
      }
      renderFocusedScene(id, scene);
    });
  }

  /**
   * Visible fraction (0..1) of a card's stage within the feed viewport.
   */
  function visibleFraction(id: string): number {
    const stage = stageEls.get(id);
    if (stage === undefined) {
      return 0;
    }
    const rootRect = scroll.getBoundingClientRect();
    const r = stage.getBoundingClientRect();
    if (r.height <= 0) {
      return 0;
    }
    const visible = Math.min(r.bottom, rootRect.bottom) - Math.max(r.top, rootRect.top);
    return Math.max(0, visible) / r.height;
  }

  /**
   * Critically-damped spring scroll to a target offset, seeded with an initial
   * velocity (px/ms) so a flick's release speed carries into the snap instead of
   * restarting from rest. snapping stays true for the whole animation so the
   * scrollend it emits is swallowed by onScrollEnd rather than re-settling.
   */
  function animateScrollTo(target: number, initialVel: number): void {
    const carriedVel = initialVel !== 0 ? initialVel : (snapping ? snapVelocity : 0);
    cancelAnimationFrame(snapRaf);
    const maxTop = Math.max(0, scroll.scrollHeight - scroll.clientHeight);
    const dest = Math.max(0, Math.min(maxTop, target));
    let pos = scroll.scrollTop;
    let vel = carriedVel;
    let last = performance.now();
    snapping = true;
    snapVelocity = carriedVel;
    const omega = 0.013;
    const step = (now: number): void => {
      let dt = now - last;
      last = now;
      if (dt > 32) {
        dt = 32;
      }
      const accel = -omega * omega * (pos - dest) - 2 * omega * vel;
      vel += accel * dt;
      pos += vel * dt;
      snapVelocity = vel;
      if (Math.abs(dest - pos) < 0.5 && Math.abs(vel) < 0.03) {
        scroll.scrollTop = dest;
        snapping = false;
        snapVelocity = 0;
        maybeCapturePreview();
        return;
      }
      scroll.scrollTop = pos;
      snapRaf = requestAnimationFrame(step);
    };
    snapRaf = requestAnimationFrame(step);
  }

  /**
   * Scrolls a card to the viewport centre (spring, carrying initialVel) and makes
   * it the focus. The feed acts as a discrete pager: focus changes only here, not
   * continuously while scrolling. A near-centred target with no inflowing
   * velocity captures the still immediately instead of running a no-op spring.
   */
  function goToCard(id: string, initialVel: number = 0): void {
    const card = cardEls.get(id);
    if (card === undefined) {
      return;
    }
    const cardRect = card.getBoundingClientRect();
    const scrollRect = scroll.getBoundingClientRect();
    const delta = (cardRect.top + cardRect.height / 2) - (scrollRect.top + scrollRect.height / 2);
    commitFocus(id);
    if (Math.abs(delta) >= 3 || Math.abs(initialVel) > 0.05) {
      animateScrollTo(scroll.scrollTop + delta, initialVel);
    } else {
      maybeCapturePreview();
    }
  }

  /**
   * Decides where a free scroll (drag / swipe) lands once it stops: if a card
   * other than the last-focused one has been revealed past the commit threshold
   * (~20%), advance to the most-revealed such card; otherwise snap back to the
   * card that was focused. velocity (px/ms) carries the flick into the snap.
   */
  function settleSnap(velocity: number = 0): void {
    if (closed || dragging || currentFocusId === null) {
      return;
    }
    let target: string | null = null;
    let bestFrac = SNAP_COMMIT_FRACTION;
    for (const id of stageEls.keys()) {
      if (id === currentFocusId) {
        continue;
      }
      const frac = visibleFraction(id);
      if (frac >= bestFrac) {
        bestFrac = frac;
        target = id;
      }
    }
    goToCard(target ?? currentFocusId, velocity);
  }

  /**
   * One card per wheel notch. preventDefault stops the native free scroll. A new
   * notch is accepted even while the previous snap is still animating (it retargets
   * the spring, carrying its current velocity) so rapid notches page ahead without
   * waiting for each correction to finish; a short cooldown only collapses the
   * burst of sub-events a single notch / trackpad frame emits.
   */
  function onWheel(ev: WheelEvent): void {
    ev.preventDefault();
    if (wheelTimer !== undefined || currentFocusId === null) {
      return;
    }
    const idx = orderedIds.indexOf(currentFocusId);
    if (idx < 0) {
      return;
    }
    const target = orderedIds[idx + (ev.deltaY > 0 ? 1 : -1)];
    if (target === undefined) {
      return;
    }
    wheelTimer = window.setTimeout((): void => {
      wheelTimer = undefined;
    }, 110);
    goToCard(target);
  }

  function onScrollEnd(): void {
    if (snapping) {
      return;
    }
    settleSnap();
  }

  function closeCardMenus(): void {
    for (const m of Array.from(scroll.querySelectorAll(".feed-card-menu.open"))) {
      m.classList.remove("open");
    }
  }

  /**
   * Opens the focused card's scene unless the consumer's guardCommit blocks it:
   * a non-null reason is shown as a toast and the feed stays open (no join, no
   * teardown) so a blocked visitor keeps browsing the live preview.
   */
  async function commit(item: T): Promise<void> {
    const block = consumer.guardCommit?.(item) ?? null;
    if (block !== null) {
      setToastMessage(block);
      return;
    }
    const scene = await prefetchScene(item.id);
    if (scene === null) {
      status.textContent = consumer.strings.loadFailed;
      return;
    }
    consumer.onCommit(item, scene);
    teardown(false);
  }

  function buildCard(item: T): HTMLDivElement {
    const card = document.createElement("div");
    card.className = "feed-card";
    card.dataset.sceneId = item.id;
    const extraCardClass = consumer.cardClassName?.(item) ?? null;
    if (extraCardClass !== null && extraCardClass.trim() !== "") {
      card.classList.add(extraCardClass);
    }

    const stage = document.createElement("div");
    stage.className = "feed-card-stage";
    stageEls.set(item.id, stage);
    const preview = document.createElement("img");
    preview.className = "feed-card-preview";
    preview.alt = "";
    const cachedPreview = previewCache.get(item.id);
    if (cachedPreview !== undefined) {
      preview.src = cachedPreview;
    }
    previewEls.set(item.id, preview);
    stage.appendChild(preview);
    const stageOverlay = consumer.buildCardStageOverlay?.(item) ?? null;
    if (stageOverlay !== null) {
      stage.appendChild(stageOverlay);
    }
    stage.addEventListener("click", (): void => void commit(item));

    const info = consumer.buildCardInfo(item);
    card.append(stage, info);
    const menu = consumer.buildCardMenu?.(item) ?? null;
    if (menu !== null) {
      card.append(menu);
    }
    return card;
  }

  async function load(): Promise<void> {
    consumer.setViewport(null);
    setToastMessage("");
    cardEls.clear();
    itemsById.clear();
    stageEls.clear();
    previewEls.clear();
    indexRows.clear();
    currentFocusId = null;
    settledId = null;
    scroll.textContent = "";
    indexList.textContent = "";
    indexCount.textContent = "";
    scroll.scrollTop = 0;
    status.textContent = consumer.strings.loading;
    const res = await consumer.loadItems();
    if (closed) {
      return;
    }
    if ("error" in res) {
      status.textContent = res.error;
      return;
    }
    const items = res.items;
    status.textContent = items.length === 0 ? consumer.strings.empty : "";
    orderedIds = items.map((i: T): string => i.id);
    for (const item of items) {
      itemsById.set(item.id, item);
      const card = buildCard(item);
      cardEls.set(item.id, card);
      scroll.appendChild(card);
      if (consumer.chrome.index) {
        const row = consumer.buildIndexRow(item);
        indexRows.set(item.id, row);
        indexList.appendChild(row);
      }
    }
    if (consumer.chrome.count) {
      indexCount.textContent = consumer.strings.indexCount(items.length);
    }
    updateFeedGap();
    if (orderedIds.length > 0) {
      commitFocus(orderedIds[0]);
      maybeCapturePreview();
    }
  }

  /**
   * Mouse drag-to-scroll for the feed: a plain overflow container only scrolls
   * with the wheel, so press-drag is wired here (touch keeps native panning, so
   * mouse only). A drag past a few px sets dragMoved, and a capture-phase click
   * listener swallows the click that would otherwise open the dragged card.
   * Pointer capture is engaged lazily once the drag actually moves, not on
   * pointerdown: capturing during a plain click retargets the synthesized click
   * to the scroll container instead of the card, which silently breaks card open.
   */
  function onDragStart(ev: PointerEvent): void {
    if (ev.pointerType !== "mouse" || ev.button !== 0) {
      return;
    }
    cancelAnimationFrame(snapRaf);
    snapping = false;
    dragging = true;
    dragMoved = false;
    dragStartY = ev.clientY;
    dragStartTop = scroll.scrollTop;
    dragPointerId = ev.pointerId;
    dragVel = 0;
    dragLastY = ev.clientY;
    dragLastT = performance.now();
  }
  function onDragMove(ev: PointerEvent): void {
    if (!dragging || ev.pointerId !== dragPointerId) {
      return;
    }
    const now = performance.now();
    const dt = now - dragLastT;
    if (dt > 0) {
      dragVel = dragVel * 0.6 + ((ev.clientY - dragLastY) / dt) * 0.4;
      dragLastY = ev.clientY;
      dragLastT = now;
    }
    const dy = ev.clientY - dragStartY;
    if (Math.abs(dy) > 3) {
      dragMoved = true;
      scroll.classList.add("feed-dragging");
      if (!scroll.hasPointerCapture(dragPointerId)) {
        scroll.setPointerCapture(dragPointerId);
      }
    }
    scroll.scrollTop = dragStartTop - dy;
  }
  function onDragEnd(ev: PointerEvent): void {
    if (!dragging || ev.pointerId !== dragPointerId) {
      return;
    }
    dragging = false;
    dragPointerId = -1;
    scroll.classList.remove("feed-dragging");
    if (scroll.hasPointerCapture(ev.pointerId)) {
      scroll.releasePointerCapture(ev.pointerId);
    }
    const exitVel = Math.max(-5, Math.min(5, -dragVel));
    settleSnap(exitVel);
  }

  scroll.addEventListener("pointerdown", onDragStart);
  scroll.addEventListener("pointermove", onDragMove);
  scroll.addEventListener("pointerup", onDragEnd);
  scroll.addEventListener("pointercancel", onDragEnd);
  scroll.addEventListener("click", (ev: MouseEvent): void => {
    if (dragMoved) {
      ev.stopPropagation();
      ev.preventDefault();
      dragMoved = false;
    }
  }, true);
  scroll.addEventListener("wheel", onWheel, { passive: false });
  scroll.addEventListener("scrollend", onScrollEnd);
  scroll.addEventListener("click", closeCardMenus);
  body.append(scroll, toast);
  overlay.append(header, body, status);
  document.body.appendChild(overlay);
  void load();
}
